import { create } from 'zustand'

import type { Capabilities, Location } from '@/api/types'

/** "All locations" is only meaningful for organisation-wide roles (ADR-029). */
export const ALL_LOCATIONS = 'all'

interface LocationState {
  /** A location id, or ALL_LOCATIONS. Null until the list is known. */
  readonly selectedId: string | null
  select: (id: string) => void
}

export const useLocationStore = create<LocationState>()((set) => ({
  selectedId: null,
  select: (id) => {
    set({ selectedId: id })
  },
}))

/**
 * The locations a caller may pick from. Scoped roles see only `locationIds`; the backend's list
 * endpoint is NOT filtered by scope (FRONTEND_PLAN BE-15), so the filter lives here.
 */
export function visibleLocations(
  locations: readonly Location[],
  capabilities: Capabilities | null,
): readonly Location[] {
  if (capabilities === null) return []
  if (!capabilities.locationScoped) return locations
  const allowed = new Set(capabilities.locationIds)
  return locations.filter((location) => allowed.has(location.id))
}

/** A sensible initial selection: the previous one if still valid, else all (org-wide) or the first. */
export function resolveSelection(
  current: string | null,
  visible: readonly Location[],
  capabilities: Capabilities | null,
): string | null {
  if (visible.length === 0) return null
  const orgWide = capabilities !== null && !capabilities.locationScoped
  if (
    current !== null &&
    (current === ALL_LOCATIONS ? orgWide : visible.some((l) => l.id === current))
  )
    return current
  return orgWide ? ALL_LOCATIONS : (visible[0]?.id ?? null)
}

/**
 * Query params for list reads. A scoped role ALWAYS sends `location_id` — an unscoped read returns
 * org-wide data (authorize.step.ts only checks the query param). Org-wide roles send it only when
 * a specific location is selected.
 */
export function scopedQuery<T extends Record<string, unknown>>(
  query: T,
  selectedId: string | null,
  capabilities: Capabilities | null,
): T & { location_id?: string } {
  if (selectedId === null || selectedId === ALL_LOCATIONS) {
    if (capabilities?.locationScoped) {
      const fallback = capabilities.locationIds[0]
      return fallback === undefined ? query : { ...query, location_id: fallback }
    }
    return query
  }
  return { ...query, location_id: selectedId }
}
